/**
 * TimeRecordsModal — the Time & Expense Records browser (admin). See docs/TIME_RECORDS.md.
 *
 * The sibling of TimeReviewModal, and deliberately NOT the same tool: Time Review is a per-person
 * workflow queue ("what do I owe a decision on"). This is a per-project record viewer/editor ("what
 * has been logged against this project, and did it reach BQE Core"). It exists because an entry that
 * has passed review but never posted is invisible in every other surface — it's out of the review
 * queue and absent from Core, so nothing shows it. Opening on the "not in Core" filter makes that
 * state the first thing you see.
 *
 * Every row renders to its lifecycle tier (services/timeRecords.js#rowCapabilities). Rows already in
 * Core are read-only on everything financial; the server enforces that independently
 * (api/time-entries.js), so this is presentation, not the guard.
 *
 * Opens on `open-time-records`. Gated to hasModule('timecard-admin') by the caller.
 */

import { useEffect, useState, useCallback, useMemo } from 'react'
import { createPortal } from 'react-dom'
import {
  getTimeRecords, getRecordProjects, getEntryAudit, saveRecordEdit, deleteRecord,
  flagRecordCorrection, clearRecordCorrection, postRecordToCore, isInCore, rowCapabilities, notInCoreReason,
  coreEntryUrl, getCoreBillStatus, reviseRecord,
} from '../services/timeRecords.js'
import { getReviewMembers, getEntryPhases, getActivities } from '../services/timecards.js'
import { confirmDialog, confirmTypeDialog, promptDialog, alertDialog } from './Dialog.jsx'

const STATUSES = ['Pending Review', 'Submitted', 'Approved', 'Post Failed', 'Posted', 'Rejected']

const money = n => '$' + (Number(n) || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
const hrs = n => (Math.round((Number(n) || 0) * 10) / 10).toFixed(1)

export default function TimeRecordsModal() {
  const [open, setOpen] = useState(false)
  const [rows, setRows] = useState(null)
  const [projects, setProjects] = useState([])
  const [members, setMembers] = useState([])
  const [busy, setBusy] = useState('')
  const [err, setErr] = useState('')
  const [expanded, setExpanded] = useState({})     // rowId -> true (detail open)
  const [auditFor, setAuditFor] = useState(null)   // { id, rows }
  const [phasesList, setPhasesList] = useState([])
  const [activitiesList, setActivitiesList] = useState([])
  const [revising, setRevising] = useState(null)   // { id, hours, clientHours, memo, billable, phaseId, activityId, entryDate }
  const [reviseErr, setReviseErr] = useState('')

  // Filters. notInCore starts ON — the tool's whole reason for existing.
  const [f, setF] = useState({ projectUid: '', memberId: '', from: '', to: '', statuses: [], notInCore: true, needsCorrection: false })

  useEffect(() => {
    function onOpen() { setOpen(true) }
    window.addEventListener('open-time-records', onOpen)
    return () => window.removeEventListener('open-time-records', onOpen)
  }, [])

  useEffect(() => {
    if (!open) return
    function onKey(e) { if (e.key === 'Escape') setOpen(false) }
    document.addEventListener('keydown', onKey)
    document.body.style.overflow = 'hidden'
    return () => { document.removeEventListener('keydown', onKey); document.body.style.overflow = '' }
  }, [open])

  const load = useCallback(async () => {
    setErr('')
    try { setRows(await getTimeRecords(f)) }
    catch (e) { setErr(e.message || 'Could not load records.'); setRows([]) }
  }, [f])

  useEffect(() => { if (open) load() }, [open, load])

  useEffect(() => {
    if (!open || projects.length) return
    getRecordProjects().then(setProjects).catch(() => {})
    getReviewMembers().then(recs => setMembers((recs || []).map(r => ({ id: r.id, name: r.fields['Member Name'] || '' })))).catch(() => {})
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open])

  // Phase/activity pickers for the Revise form — loaded once, lazily, same as projects/members above.
  useEffect(() => {
    if (!open || phasesList.length) return
    getEntryPhases().then(setPhasesList).catch(() => {})
    getActivities().then(setActivitiesList).catch(() => {})
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open])

  /* Reconciliation summary — computed from the SAME rows the table renders, so the strip can never
   * claim something the list below it contradicts. */
  const summary = useMemo(() => {
    const s = { inCore: { n: 0, h: 0, amt: 0 }, notInCore: { n: 0, h: 0, amt: 0 }, byReason: {} }
    for (const r of (rows || [])) {
      const bucket = isInCore(r) ? s.inCore : s.notInCore
      bucket.n++; bucket.h += Number(r.actual_hours) || 0; bucket.amt += Number(r.charge_amount) || 0
      if (!isInCore(r)) {
        const why = notInCoreReason(r)
        s.byReason[why] = s.byReason[why] || { n: 0, h: 0, amt: 0 }
        s.byReason[why].n++; s.byReason[why].h += Number(r.actual_hours) || 0
        s.byReason[why].amt += Number(r.charge_amount) || 0
      }
    }
    return s
  }, [rows])

  // Grouped project → phase, preserving the server's date ordering inside each group.
  const groups = useMemo(() => {
    const byProject = new Map()
    for (const r of (rows || [])) {
      const pk = r.project_name || '(no project)'
      if (!byProject.has(pk)) byProject.set(pk, new Map())
      const phases = byProject.get(pk)
      const ph = r.phase_name || '(no phase)'
      if (!phases.has(ph)) phases.set(ph, [])
      phases.get(ph).push(r)
    }
    return [...byProject.entries()]
      .sort((a, b) => a[0].localeCompare(b[0]))
      .map(([project, phases]) => ({
        project,
        phases: [...phases.entries()].sort((a, b) => a[0].localeCompare(b[0])).map(([phase, entries]) => ({ phase, entries })),
        entries: [...phases.values()].flat(),
      }))
  }, [rows])

  const postable = useMemo(() => (rows || []).filter(r => rowCapabilities(r).canPost), [rows])

  async function doPost(row) {
    setBusy(row.id)
    try {
      const r = await postRecordToCore(row.id)
      if (!r || r.success !== true) {
        await alertDialog({ title: 'Post failed', message: (r && (r.error || r.message)) || 'BQE Core did not accept this entry.' })
      }
      await load()
    } finally { setBusy('') }
  }

  async function doPostAll() {
    if (!postable.length) return
    const ok = await confirmDialog({
      title: 'Post to BQE Core?',
      message: `${postable.length} ${postable.length === 1 ? 'entry' : 'entries'} (${hrs(postable.reduce((a, r) => a + (Number(r.actual_hours) || 0), 0))}h) will be posted to BQE Core now.`,
      confirmLabel: 'Post to Core',
    })
    if (!ok) return
    setBusy('__all__')
    let failed = 0
    try {
      for (const r of postable) {
        const res = await postRecordToCore(r.id)
        if (!res || res.success !== true) failed++
      }
      await load()
      if (failed) await alertDialog({ title: 'Some entries did not post', message: `${failed} of ${postable.length} were rejected by BQE Core. Open each one to see why.` })
    } finally { setBusy('') }
  }

  async function doFlag(row) {
    const note = await promptDialog({
      title: 'Flag for correction in Core',
      message: 'This entry is already in BQE Core, so it cannot be changed here. Describe what is wrong so whoever fixes it in Core knows what to do.',
      placeholder: 'e.g. Wrong project — belongs on 26-04 Sunday Goods, not overhead',
      multiline: true, required: true,
    })
    if (note === null) return
    setBusy(row.id)
    try { await flagRecordCorrection(row.id, note); await load() } finally { setBusy('') }
  }

  async function doClearFlag(row) {
    setBusy(row.id)
    try { await clearRecordCorrection(row.id); await load() } finally { setBusy('') }
  }

  /* Revise a Posted entry. Checks BQE Core live FIRST — the whole point is that a billed entry
   * must never even see an edit form, it goes to Flag for correction instead. */
  async function openRevise(row) {
    setBusy(row.id)
    setReviseErr('')
    try {
      const status = await getCoreBillStatus(row.id)
      if (status && status.billed) {
        await alertDialog({
          title: 'Already billed in Core',
          message: `This entry has already been billed on invoice ${status.invoiceNumber || status.invoiceId} in BQE Core. ` +
            `Billed entries can't be revised here — use Flag for correction instead, and fix the invoice in Core.`,
        })
        return
      }
      const prefill = {
        hours: row.actual_hours != null ? String(row.actual_hours) : '',
        clientHours: row.client_hours != null ? String(row.client_hours) : '',
        memo: row.memo || '',
        billable: !!row.billable,
        phaseId: row.phase_airtable_id || '',
        activityId: row.activity_code_id || '',
        entryDate: row.entry_date || '',
      }
      setRevising({ id: row.id, ...prefill, _orig: prefill })
    } catch (e) {
      await alertDialog({ title: 'Could not check BQE Core', message: e.message || 'Try again.' })
    } finally { setBusy('') }
  }

  function cancelRevise() { setRevising(null); setReviseErr('') }

  async function saveRevise() {
    if (!revising) return
    // Only send fields that actually changed from what the form opened with — this is what keeps
    // an untouched Phase/Activity from ever resolving to "clear it" (an empty select value means
    // "user picked nothing", never "field not touched" — those are different things, and a Posted
    // entry always needs a phase). Mirrors saveEdit's own "only touch fields provided" rule.
    const orig = revising._orig || {}
    const patch = {}
    if (revising.hours !== orig.hours) patch.hours = revising.hours === '' ? 0 : Number(revising.hours)
    if (revising.clientHours !== orig.clientHours) patch.clientHours = revising.clientHours === '' ? null : Number(revising.clientHours)
    if (revising.memo !== orig.memo) patch.memo = revising.memo
    if (revising.billable !== orig.billable) patch.billable = revising.billable
    if (revising.phaseId !== orig.phaseId) patch.phaseId = revising.phaseId || null
    if (revising.activityId !== orig.activityId) patch.activityId = revising.activityId || null
    if (revising.entryDate !== orig.entryDate) patch.entryDate = revising.entryDate
    if (!Object.keys(patch).length) { setReviseErr('Nothing changed.'); return }
    setBusy(revising.id)
    setReviseErr('')
    try {
      const r = await reviseRecord(revising.id, patch)
      if (r && r.error) {
        if (r.billed) {
          await alertDialog({ title: 'Already billed in Core', message: r.error })
          setRevising(null)
        } else {
          setReviseErr(r.error)
        }
        return
      }
      setRevising(null)
      await load()
    } finally { setBusy('') }
  }

  async function doDelete(row) {
    const signedOff = row.status === 'Approved' || row.status === 'Post Failed'
    const ok = signedOff
      ? await confirmTypeDialog({
          title: 'Delete an approved entry?',
          message: `${row.member_name} · ${row.entry_date} · ${hrs(row.actual_hours)}h on ${row.project_name || 'no project'}. This entry was already approved. Deleting it cannot be undone.`,
        })
      : await confirmDialog({
          title: 'Delete this entry?',
          message: `${row.member_name} · ${row.entry_date} · ${hrs(row.actual_hours)}h. This cannot be undone.`,
          confirmLabel: 'Delete', danger: true,
        })
    if (!ok) return
    setBusy(row.id)
    try {
      const r = await deleteRecord(row.id)
      if (r && r.error) await alertDialog({ title: 'Could not delete', message: r.error })
      await load()
    } finally { setBusy('') }
  }

  async function saveNotes(row, reviewNotes) {
    setBusy(row.id)
    try {
      const r = await saveRecordEdit(row.id, { reviewNotes })
      if (r && r.error) await alertDialog({ title: 'Could not save', message: r.error })
      await load()
    } finally { setBusy('') }
  }

  async function showAudit(row) {
    try { setAuditFor({ id: row.id, rows: await getEntryAudit(row.id) }) }
    catch { setAuditFor({ id: row.id, rows: [] }) }
  }

  function toggleStatus(s) {
    setF(prev => ({ ...prev, statuses: prev.statuses.includes(s) ? prev.statuses.filter(x => x !== s) : [...prev.statuses, s] }))
  }

  if (!open) return null

  return createPortal(
    <div style={S.overlay}>
      <div style={S.modal}>
        <div style={S.bar}>
          <span style={S.title}>Time &amp; Expense Records</span>
          <button style={S.close} title="Close (Esc)" onClick={() => setOpen(false)}>&times;</button>
        </div>

        {/* Filters */}
        <div style={S.filters}>
          <select style={S.select} value={f.projectUid} onChange={e => setF({ ...f, projectUid: e.target.value })}>
            <option value="">All projects</option>
            {projects.map(p => <option key={p.projectUid} value={p.projectUid}>{p.projectName} ({p.entries})</option>)}
          </select>
          <select style={S.select} value={f.memberId} onChange={e => setF({ ...f, memberId: e.target.value })}>
            <option value="">Everyone</option>
            {members.map(m => <option key={m.id} value={m.id}>{m.name}</option>)}
          </select>
          <input style={S.date} type="date" value={f.from} onChange={e => setF({ ...f, from: e.target.value })} title="From" />
          <input style={S.date} type="date" value={f.to} onChange={e => setF({ ...f, to: e.target.value })} title="To" />
          <label style={S.toggle}>
            <input type="checkbox" checked={f.notInCore} onChange={e => setF({ ...f, notInCore: e.target.checked })} />
            Not in Core
          </label>
          <label style={S.toggle}>
            <input type="checkbox" checked={f.needsCorrection} onChange={e => setF({ ...f, needsCorrection: e.target.checked })} />
            Flagged
          </label>
        </div>
        <div style={S.pillRow}>
          {STATUSES.map(s => (
            <button key={s} onClick={() => toggleStatus(s)}
              style={{ ...S.pill, ...(f.statuses.includes(s) ? S.pillOn : null) }}>{s}</button>
          ))}
          {(f.statuses.length > 0 || f.projectUid || f.memberId || f.from || f.to || f.needsCorrection || !f.notInCore) && (
            <button style={S.reset} onClick={() => setF({ projectUid: '', memberId: '', from: '', to: '', statuses: [], notInCore: true, needsCorrection: false })}>
              Reset
            </button>
          )}
        </div>

        {/* Reconciliation strip */}
        <div style={S.recon}>
          <div style={S.reconCol}>
            <div style={S.reconLbl}>In BQE Core</div>
            <div style={S.reconBig}>{summary.inCore.n} <span style={S.reconUnit}>entries</span></div>
            <div style={S.reconSub}>{hrs(summary.inCore.h)}h · {money(summary.inCore.amt)}</div>
          </div>
          <div style={{ ...S.reconCol, ...S.reconWarnCol }}>
            <div style={S.reconLbl}>Not in Core</div>
            <div style={{ ...S.reconBig, color: summary.notInCore.n ? '#b45309' : 'var(--aj-text)' }}>
              {summary.notInCore.n} <span style={S.reconUnit}>entries</span>
            </div>
            <div style={S.reconSub}>{hrs(summary.notInCore.h)}h · {money(summary.notInCore.amt)}</div>
          </div>
          <div style={S.reasons}>
            {Object.keys(summary.byReason).length === 0
              ? <div style={S.reasonNone}>Everything in view has reached Core.</div>
              : Object.entries(summary.byReason).sort((a, b) => b[1].amt - a[1].amt).map(([why, v]) => (
                  <div key={why} style={S.reasonRow}>
                    <span style={S.reasonWhy}>{why}</span>
                    <span style={S.reasonNum}>{v.n} · {hrs(v.h)}h · {money(v.amt)}</span>
                  </div>
                ))}
          </div>
          {postable.length > 0 && (
            <button style={S.postAll} onClick={doPostAll} disabled={busy === '__all__'}>
              {busy === '__all__' ? 'Posting…' : `Post ${postable.length} to Core`}
            </button>
          )}
        </div>

        {/* Records */}
        <div style={S.list}>
          {err && <div style={S.err}>{err}</div>}
          {rows === null && <div style={S.empty}>Loading records…</div>}
          {rows && rows.length === 0 && <div style={S.empty}>No entries match these filters.</div>}
          {groups.map(g => {
            const gh = g.entries.reduce((a, r) => a + (Number(r.actual_hours) || 0), 0)
            const gAmt = g.entries.reduce((a, r) => a + (Number(r.charge_amount) || 0), 0)
            const gOut = g.entries.filter(r => !isInCore(r)).length
            return (
              <div key={g.project} style={S.group}>
                <div style={S.groupHead}>
                  <span style={S.groupName}>{g.project}</span>
                  <span style={S.groupMeta}>
                    {g.entries.length} · {hrs(gh)}h · {money(gAmt)}
                    {gOut > 0 && <span style={S.groupOut}>{gOut} not in Core</span>}
                  </span>
                </div>
                {g.phases.map(ph => (
                  <div key={ph.phase}>
                    <div style={S.phaseHead}>{ph.phase}</div>
                    {ph.entries.map(r => {
                      const cap = rowCapabilities(r)
                      const isOpen = !!expanded[r.id]
                      return (
                        <div key={r.id} style={{ ...S.row, ...(cap.inCore ? S.rowLocked : S.rowOpen) }}>
                          <div style={S.rowTop} onClick={() => setExpanded(p => ({ ...p, [r.id]: !p[r.id] }))}>
                            <span style={S.rDate}>{r.entry_date}</span>
                            <span style={S.rWho}>{r.member_name}</span>
                            <span style={S.rHrs}>{hrs(r.actual_hours)}h</span>
                            <span style={S.rAmt}>{r.billable ? money(r.charge_amount) : 'non-bill'}</span>
                            <span style={{ ...S.rStatus, ...statusStyle(r.status) }}>{r.status}</span>
                            {cap.inCore
                              ? <span style={S.chipCore} title={'Core entry ' + (r.core_entry_id || '')}>In Core</span>
                              : <span style={S.chipOut}>{notInCoreReason(r)}</span>}
                            {r.needs_correction && <span style={S.chipFlag}>Flagged</span>}
                          </div>
                          {isOpen && (
                            <div style={S.detail}>
                              <div style={S.detailMemo}>{r.memo || <span style={S.muted}>No memo</span>}</div>
                              <div style={S.detailMeta}>
                                {r.activity_code_string && <span>{r.activity_code_string}</span>}
                                {r.bill_rate != null && <span>rate {money(r.bill_rate)}</span>}
                                {r.client_hours != null && r.client_hours !== r.actual_hours && (
                                  <span style={{ color: '#b45309' }}>{hrs(r.client_hours)}h billed to Core (write-{r.client_hours < r.actual_hours ? 'down' : 'up'})</span>
                                )}
                                {r.date_sent_to_core && <span>posted {String(r.date_sent_to_core).slice(0, 10)}</span>}
                                {r.core_entry_id && (
                                  <a href={coreEntryUrl(r.core_entry_id)} target="_blank" rel="noopener noreferrer" style={S.coreLink}
                                    title="Read-only reference — this app never writes back to a posted entry">
                                    ↗ Open in BQE Core
                                  </a>
                                )}
                              </div>
                              {r.post_error && <div style={S.postErr}>{r.post_error}</div>}
                              {r.rejection_notes && <div style={S.rejNote}>Sent back: {r.rejection_notes}</div>}
                              {r.needs_correction && (
                                <div style={S.flagNote}>
                                  Flagged for correction in Core{r.correction_note ? ': ' + r.correction_note : ''}
                                </div>
                              )}
                              {cap.inCore && !(revising && revising.id === r.id) && (
                                <div style={S.lockNote}>
                                  This entry is in BQE Core. Core is what the client is billed from, so hours,
                                  memo, billable, phase, and activity are read-only here. If it hasn't been
                                  billed on an invoice yet, use Revise to fix it (updates Core and Cortex
                                  together); once it's billed, Revise refuses and Flag for correction is the
                                  only path.
                                </div>
                              )}
                              {revising && revising.id === r.id ? (
                                <ReviseForm
                                  revising={revising} setRevising={setRevising}
                                  phasesList={phasesList} activitiesList={activitiesList}
                                  err={reviseErr} busy={busy === r.id}
                                  onSave={saveRevise} onCancel={cancelRevise}
                                />
                              ) : (
                                <>
                                  <NotesEditor row={r} onSave={saveNotes} busy={busy === r.id} />
                                  <div style={S.actions}>
                                    {cap.canPost && (
                                      <button style={S.btnPrimary} onClick={() => doPost(r)} disabled={busy === r.id}>
                                        {busy === r.id ? '…' : (r.status === 'Post Failed' ? 'Retry post to Core' : 'Post to Core')}
                                      </button>
                                    )}
                                    {cap.canRevise && (
                                      <button style={S.btn} onClick={() => openRevise(r)} disabled={busy === r.id}>
                                        {busy === r.id ? '…' : 'Revise'}
                                      </button>
                                    )}
                                    {cap.canFlagCorrection && !r.needs_correction && (
                                      <button style={S.btn} onClick={() => doFlag(r)} disabled={busy === r.id}>Flag for correction</button>
                                    )}
                                    {r.needs_correction && (
                                      <button style={S.btn} onClick={() => doClearFlag(r)} disabled={busy === r.id}>Clear flag</button>
                                    )}
                                    <button style={S.btn} onClick={() => showAudit(r)}>History</button>
                                    {cap.canDelete && (
                                      <button style={S.btnDanger} onClick={() => doDelete(r)} disabled={busy === r.id}>Delete</button>
                                    )}
                                  </div>
                                </>
                              )}
                            </div>
                          )}
                        </div>
                      )
                    })}
                  </div>
                ))}
              </div>
            )
          })}
        </div>

        {auditFor && (
          <div style={S.auditScrim} onClick={() => setAuditFor(null)}>
            <div style={S.auditCard} onClick={e => e.stopPropagation()}>
              <div style={S.auditHead}>Change history</div>
              {auditFor.rows.length === 0
                ? <div style={S.muted}>No recorded changes. Entries edited before this tool shipped have no history.</div>
                : auditFor.rows.map(a => (
                    <div key={a.id} style={S.auditRow}>
                      <div style={S.auditWhen}>{new Date(a.created_at).toLocaleString()} · {a.action}</div>
                      {a.changes && <pre style={S.auditJson}>{JSON.stringify(a.changes, null, 1)}</pre>}
                    </div>
                  ))}
              <button style={S.btn} onClick={() => setAuditFor(null)}>Close</button>
            </div>
          </div>
        )}
      </div>
    </div>,
    document.body
  )
}

/**
 * The Revise form — only ever mounted after openRevise() has confirmed (live, against Core) that
 * this entry isn't billed yet. Saving still goes through the server's own re-check (see
 * api/time-entries.js#revisePosted), so a race where it gets billed between opening this form and
 * clicking Save is caught there, not assumed impossible here.
 */
function ReviseForm({ revising, setRevising, phasesList, activitiesList, err, busy, onSave, onCancel }) {
  const set = (k, v) => setRevising(prev => ({ ...prev, [k]: v }))
  return (
    <div style={S.reviseBox}>
      <div style={S.reviseNote}>
        Not yet billed in Core — revising here updates both BQE Core and Cortex together.
      </div>
      <div style={S.reviseGrid}>
        <label style={S.reviseField}>
          <span style={S.reviseLbl}>Date</span>
          <input style={S.reviseInput} type="date" value={revising.entryDate} onChange={e => set('entryDate', e.target.value)} disabled={busy} />
        </label>
        <label style={S.reviseField}>
          <span style={S.reviseLbl}>Hours</span>
          <input style={S.reviseInput} type="number" step="0.1" min="0" value={revising.hours} onChange={e => set('hours', e.target.value)} disabled={busy} />
        </label>
        <label style={S.reviseField}>
          <span style={S.reviseLbl}>Client hours (blank = follow Hours)</span>
          <input style={S.reviseInput} type="number" step="0.1" min="0" placeholder="—" value={revising.clientHours} onChange={e => set('clientHours', e.target.value)} disabled={busy} />
        </label>
        <label style={{ ...S.reviseField, flexDirection: 'row', alignItems: 'center', gap: 6 }}>
          <input type="checkbox" checked={revising.billable} onChange={e => set('billable', e.target.checked)} disabled={busy} />
          <span style={S.reviseLbl}>Billable</span>
        </label>
        <label style={S.reviseField}>
          <span style={S.reviseLbl}>Phase</span>
          <select style={S.reviseInput} value={revising.phaseId} onChange={e => set('phaseId', e.target.value)} disabled={busy}>
            {/* A Posted entry always has a phase in Core, so there's no "clear it" option here — if
               the entry's current phase isn't in this curated list, pin it as a placeholder so the
               select stays on "nothing changed" until the reviewer deliberately picks a new one. */}
            {!phasesList.some(p => p.id === revising.phaseId) && <option value={revising.phaseId}>(current phase)</option>}
            {phasesList.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
          </select>
        </label>
        <label style={S.reviseField}>
          <span style={S.reviseLbl}>Activity</span>
          <select style={S.reviseInput} value={revising.activityId} onChange={e => set('activityId', e.target.value)} disabled={busy}>
            {!activitiesList.some(a => a.id === revising.activityId) && <option value={revising.activityId}>(current activity)</option>}
            {activitiesList.map(a => <option key={a.id} value={a.id}>{a.label}</option>)}
          </select>
        </label>
      </div>
      <label style={{ ...S.reviseField, marginTop: 8 }}>
        <span style={S.reviseLbl}>Memo</span>
        <textarea style={{ ...S.reviseInput, minHeight: 50, resize: 'vertical' }} value={revising.memo} onChange={e => set('memo', e.target.value)} disabled={busy} />
      </label>
      {err && <div style={S.postErr}>{err}</div>}
      <div style={S.actions}>
        <button style={S.btnPrimary} onClick={onSave} disabled={busy}>{busy ? 'Saving…' : 'Save to Core & Cortex'}</button>
        <button style={S.btn} onClick={onCancel} disabled={busy}>Cancel</button>
      </div>
    </div>
  )
}

/** Review notes stay editable at every tier — they never went to Core. */
function NotesEditor({ row, onSave, busy }) {
  const [v, setV] = useState(row.review_notes || '')
  const dirty = v !== (row.review_notes || '')
  return (
    <div style={S.notesWrap}>
      <input style={S.notesInput} value={v} placeholder="Internal note (never sent to Core)"
        onChange={e => setV(e.target.value)} disabled={busy} />
      {dirty && <button style={S.btnPrimary} onClick={() => onSave(row, v)} disabled={busy}>{busy ? '…' : 'Save note'}</button>}
    </div>
  )
}

function statusStyle(s) {
  if (s === 'Posted') return { color: '#1e6b47', background: 'rgba(30,107,71,0.10)' }
  if (s === 'Post Failed') return { color: '#c0392b', background: 'rgba(192,57,43,0.10)' }
  if (s === 'Rejected') return { color: '#c0392b', background: 'rgba(192,57,43,0.10)' }
  if (s === 'Approved') return { color: '#b45309', background: 'rgba(180,83,9,0.12)' }
  return { color: 'var(--aj-text-mid)', background: 'var(--aj-bg-muted)' }
}

const MONO = '"DM Mono", monospace'
const SANS = '"DM Sans", sans-serif'

const S = {
  overlay: { position: 'fixed', inset: 0, zIndex: 720, background: 'rgba(4,21,24,0.55)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 },
  modal: { width: '100%', maxWidth: 1080, maxHeight: 'calc(100vh - 40px)', background: 'var(--aj-bg-elevated)', border: '0.5px solid var(--aj-border)', borderRadius: 10, display: 'flex', flexDirection: 'column', minHeight: 0, overflow: 'hidden' },
  bar: { flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: '#07272D', padding: '10px 14px' },
  title: { fontFamily: '"Montserrat", sans-serif', fontSize: 12, fontWeight: 500, letterSpacing: '0.14em', textTransform: 'uppercase', color: '#fff' },
  close: { background: 'transparent', border: 'none', color: '#fff', fontSize: 22, lineHeight: 1, cursor: 'pointer' },

  filters: { flexShrink: 0, display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center', padding: '12px 14px 8px' },
  select: { fontFamily: SANS, fontSize: 12, color: 'var(--aj-text)', background: 'var(--aj-bg-page)', border: '0.5px solid var(--aj-border-mid)', borderRadius: 4, padding: '6px 8px', maxWidth: 300 },
  date: { fontFamily: MONO, fontSize: 12, color: 'var(--aj-text)', background: 'var(--aj-bg-page)', border: '0.5px solid var(--aj-border-mid)', borderRadius: 4, padding: '5px 8px' },
  toggle: { display: 'flex', alignItems: 'center', gap: 5, fontFamily: SANS, fontSize: 12, color: 'var(--aj-text)', cursor: 'pointer' },
  pillRow: { flexShrink: 0, display: 'flex', flexWrap: 'wrap', gap: 6, padding: '0 14px 12px' },
  pill: { fontFamily: SANS, fontSize: 11, color: 'var(--aj-text-mid)', background: 'var(--aj-bg-subtle)', border: '0.5px solid var(--aj-border)', borderRadius: 20, padding: '3px 10px', cursor: 'pointer' },
  pillOn: { background: '#07272D', color: '#fff', borderColor: '#07272D' },
  reset: { fontFamily: SANS, fontSize: 11, color: 'var(--aj-text-muted)', background: 'transparent', border: 'none', textDecoration: 'underline', cursor: 'pointer' },

  recon: { flexShrink: 0, display: 'flex', gap: 18, alignItems: 'flex-start', margin: '0 14px 12px', padding: '12px 14px', background: 'var(--aj-bg-subtle)', border: '0.5px solid var(--aj-border)', borderRadius: 8 },
  reconCol: { flexShrink: 0, minWidth: 110 },
  reconWarnCol: { paddingLeft: 18, borderLeft: '0.5px solid var(--aj-border)' },
  reconLbl: { fontFamily: '"Montserrat", sans-serif', fontSize: 9, fontWeight: 600, letterSpacing: '0.12em', textTransform: 'uppercase', color: 'var(--aj-text-muted)' },
  reconBig: { fontFamily: MONO, fontSize: 20, color: 'var(--aj-text)', marginTop: 3 },
  reconUnit: { fontFamily: SANS, fontSize: 11, color: 'var(--aj-text-muted)' },
  reconSub: { fontFamily: MONO, fontSize: 11, color: 'var(--aj-text-mid)', marginTop: 2 },
  reasons: { flex: '1 1 auto', minWidth: 0, paddingLeft: 18, borderLeft: '0.5px solid var(--aj-border)' },
  reasonRow: { display: 'flex', justifyContent: 'space-between', gap: 12, fontSize: 11, padding: '1px 0' },
  reasonWhy: { fontFamily: SANS, color: 'var(--aj-text-mid)' },
  reasonNum: { fontFamily: MONO, color: 'var(--aj-text)' },
  reasonNone: { fontFamily: SANS, fontSize: 11, color: 'var(--aj-text-muted)' },
  postAll: { flexShrink: 0, alignSelf: 'center', fontFamily: SANS, fontSize: 12, fontWeight: 600, color: '#fff', background: '#07272D', border: 'none', borderRadius: 6, padding: '9px 14px', cursor: 'pointer' },

  list: { flex: '1 1 auto', overflowY: 'auto', minHeight: 0, padding: '0 14px 16px' },
  empty: { fontFamily: SANS, fontSize: 13, color: 'var(--aj-text-muted)', padding: '20px 0', textAlign: 'center' },
  err: { fontFamily: SANS, fontSize: 12, color: '#c0392b', padding: '8px 0' },

  group: { marginBottom: 14 },
  groupHead: { display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 12, padding: '6px 0 4px', borderBottom: '0.5px solid var(--aj-border-mid)' },
  groupName: { fontFamily: '"Montserrat", sans-serif', fontSize: 11, fontWeight: 600, letterSpacing: '0.06em', textTransform: 'uppercase', color: 'var(--aj-text)' },
  groupMeta: { fontFamily: MONO, fontSize: 11, color: 'var(--aj-text-mid)', display: 'flex', gap: 10, alignItems: 'center' },
  groupOut: { color: '#b45309', background: 'rgba(180,83,9,0.12)', borderRadius: 10, padding: '1px 7px' },
  phaseHead: { fontFamily: SANS, fontSize: 11, color: 'var(--aj-text-muted)', padding: '7px 0 3px' },

  row: { borderRadius: 6, marginBottom: 3, border: '0.5px solid var(--aj-border)' },
  rowOpen: { background: 'var(--aj-bg-page)' },
  rowLocked: { background: 'var(--aj-bg-subtle)' },
  rowTop: { display: 'flex', alignItems: 'center', gap: 10, padding: '6px 10px', cursor: 'pointer', flexWrap: 'wrap' },
  rDate: { fontFamily: MONO, fontSize: 11, color: 'var(--aj-text-mid)', width: 78, flexShrink: 0 },
  rWho: { fontFamily: SANS, fontSize: 12, color: 'var(--aj-text)', flex: '1 1 130px', minWidth: 0 },
  rHrs: { fontFamily: MONO, fontSize: 12, color: 'var(--aj-text)', width: 46, textAlign: 'right', flexShrink: 0 },
  rAmt: { fontFamily: MONO, fontSize: 11, color: 'var(--aj-text-mid)', width: 84, textAlign: 'right', flexShrink: 0 },
  rStatus: { fontFamily: SANS, fontSize: 10, borderRadius: 10, padding: '1px 8px', flexShrink: 0 },
  chipCore: { fontFamily: SANS, fontSize: 10, color: '#1e6b47', background: 'rgba(30,107,71,0.10)', borderRadius: 10, padding: '1px 8px', flexShrink: 0 },
  chipOut: { fontFamily: SANS, fontSize: 10, color: '#b45309', background: 'rgba(180,83,9,0.12)', borderRadius: 10, padding: '1px 8px', flexShrink: 0 },
  chipFlag: { fontFamily: SANS, fontSize: 10, color: '#c0392b', background: 'rgba(192,57,43,0.10)', borderRadius: 10, padding: '1px 8px', flexShrink: 0 },

  detail: { padding: '2px 10px 10px', borderTop: '0.5px solid var(--aj-border)' },
  detailMemo: { fontFamily: SANS, fontSize: 12, color: 'var(--aj-text)', lineHeight: 1.5, padding: '8px 0 4px', whiteSpace: 'pre-wrap' },
  detailMeta: { display: 'flex', flexWrap: 'wrap', gap: 12, fontFamily: MONO, fontSize: 10, color: 'var(--aj-text-muted)', paddingBottom: 6 },
  coreLink: { fontFamily: SANS, color: 'var(--aj-accent-blue)', textDecoration: 'none' },
  postErr: { fontFamily: MONO, fontSize: 10, color: '#c0392b', background: 'rgba(192,57,43,0.07)', borderRadius: 4, padding: '6px 8px', marginBottom: 6, wordBreak: 'break-word' },
  rejNote: { fontFamily: SANS, fontSize: 11, color: '#c0392b', paddingBottom: 6 },
  flagNote: { fontFamily: SANS, fontSize: 11, color: '#c0392b', background: 'rgba(192,57,43,0.07)', borderRadius: 4, padding: '6px 8px', marginBottom: 6 },
  lockNote: { fontFamily: SANS, fontSize: 11, color: 'var(--aj-text-mid)', background: 'var(--aj-bg-muted)', borderRadius: 4, padding: '7px 9px', marginBottom: 8, lineHeight: 1.5 },
  reviseBox: { border: '0.5px solid var(--aj-accent-blue)', borderRadius: 6, padding: 10, marginBottom: 8, background: 'var(--aj-bg-page)' },
  reviseNote: { fontFamily: SANS, fontSize: 11, color: 'var(--aj-text-mid)', paddingBottom: 8 },
  reviseGrid: { display: 'flex', flexWrap: 'wrap', gap: 10 },
  reviseField: { display: 'flex', flexDirection: 'column', gap: 3, minWidth: 140, flex: '1 1 140px' },
  reviseLbl: { fontFamily: SANS, fontSize: 10, color: 'var(--aj-text-muted)' },
  reviseInput: { fontFamily: SANS, fontSize: 12, color: 'var(--aj-text)', background: 'var(--aj-bg-elevated)', border: '0.5px solid var(--aj-border-mid)', borderRadius: 4, padding: '6px 8px' },
  notesWrap: { display: 'flex', gap: 6, alignItems: 'center', paddingBottom: 8 },
  notesInput: { flex: '1 1 auto', fontFamily: SANS, fontSize: 12, color: 'var(--aj-text)', background: 'var(--aj-bg-elevated)', border: '0.5px solid var(--aj-border-mid)', borderRadius: 4, padding: '6px 8px' },
  actions: { display: 'flex', flexWrap: 'wrap', gap: 6 },
  btn: { fontFamily: SANS, fontSize: 11, color: 'var(--aj-text-mid)', background: 'transparent', border: '0.5px solid var(--aj-border-mid)', borderRadius: 4, padding: '5px 10px', cursor: 'pointer' },
  btnPrimary: { fontFamily: SANS, fontSize: 11, fontWeight: 600, color: '#fff', background: '#07272D', border: 'none', borderRadius: 4, padding: '6px 11px', cursor: 'pointer' },
  btnDanger: { fontFamily: SANS, fontSize: 11, color: '#c0392b', background: 'transparent', border: '0.5px solid rgba(192,57,43,0.4)', borderRadius: 4, padding: '5px 10px', cursor: 'pointer' },
  muted: { fontFamily: SANS, fontSize: 12, color: 'var(--aj-text-muted)' },

  auditScrim: { position: 'fixed', inset: 0, background: 'rgba(4,21,24,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 },
  auditCard: { width: '100%', maxWidth: 520, maxHeight: '70vh', overflowY: 'auto', background: 'var(--aj-bg-elevated)', border: '0.5px solid var(--aj-border)', borderRadius: 10, padding: 18 },
  auditHead: { fontFamily: '"Montserrat", sans-serif', fontSize: 11, fontWeight: 600, letterSpacing: '0.1em', textTransform: 'uppercase', color: 'var(--aj-text)', marginBottom: 12 },
  auditRow: { paddingBottom: 10, marginBottom: 10, borderBottom: '0.5px solid var(--aj-border)' },
  auditWhen: { fontFamily: MONO, fontSize: 11, color: 'var(--aj-text-mid)' },
  auditJson: { fontFamily: MONO, fontSize: 10, color: 'var(--aj-text-muted)', margin: '4px 0 0', whiteSpace: 'pre-wrap', wordBreak: 'break-word' },
}
